/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — docx-helpers.ts
   Shared formatting helpers, style constants, and reusable builders for
   the Word (.docx) export pipeline.
   ═══════════════════════════════════════════════════════════════════════ */

import {
  Paragraph, Table, TableRow, TableCell, TextRun,
  AlignmentType, WidthType, BorderStyle,
  type ITableCellBorders,
} from 'docx';
import { meta, extractYear, cur } from './utils';

// ─── Formatting Helpers ──────────────────────────────────────────────────────

/**
 * DOCX-specific amount formatter — intentionally NOT the same as fmt() in utils.ts.
 * Differences from fmt():
 *   1. Always uses 2 decimal places (ignores template decimals setting)
 *   2. Always uses parentheses for negatives (ignores template negativeStyle)
 *   3. Ignores "in thousands" presentation mode (DOCX needs exact figures)
 *   4. Prepends the currency symbol and handles null/undefined inputs
 * This ensures Word exports produce consistent, template-independent formatting.
 */
export function fmtAmt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '';
  const c = cur();
  const abs = Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return n < 0 ? '(' + c + abs + ')' : c + abs;
}

// ─── Reusable DOCX Builders ────────────────────────────────────────────────

export const BORDER_NONE: ITableCellBorders = {
  top: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
  bottom: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
  left: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
  right: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
};

export const BORDER_BOTTOM: ITableCellBorders = {
  top: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
  bottom: { style: BorderStyle.SINGLE, size: 1, color: '1E293B' },
  left: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
  right: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
};

export const BORDER_DOUBLE_BOTTOM: ITableCellBorders = {
  top: { style: BorderStyle.SINGLE, size: 1, color: '1E293B' },
  bottom: { style: BorderStyle.DOUBLE, size: 1, color: '1E293B' },
  left: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
  right: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
};

export function stmtHeaderParagraphs(
  company: string,
  title: string,
  periodText: string,
): Paragraph[] {
  return [
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 40 },
      children: [new TextRun({ text: company, bold: true, size: 24, font: 'Calibri' })],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 40 },
      children: [new TextRun({ text: title, bold: true, size: 22, font: 'Calibri' })],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 },
      children: [new TextRun({ text: periodText, italics: true, size: 20, font: 'Calibri' })],
    }),
  ];
}

export function isTotal(label: string): boolean {
  const lbl = label.trim();
  return lbl.startsWith('Total') || lbl.startsWith('TOTAL') ||
    lbl === 'Gross Profit' || lbl === 'Operating Income' || lbl === 'Net Income' ||
    lbl === 'Net Change in Cash';
}

export function isSectionHeader(label: string): boolean {
  const lbl = label.trim();
  return (lbl === 'ASSETS' || lbl === 'LIABILITIES & EQUITY' ||
    lbl.startsWith('Cash Flows from') ||
    lbl === 'Revenue' || lbl === 'Cost of Goods Sold' ||
    lbl === 'Operating Expenses' || lbl.startsWith('Other Income') ||
    lbl === 'Current Assets' || lbl === 'Non-Current Assets' ||
    lbl === 'Current Liabilities' || lbl === 'Non-Current Liabilities' ||
    lbl === "Shareholders' Equity" ||
    lbl === 'Prior Period' || lbl === 'Current Period' ||
    lbl.indexOf('Adjustments for non-cash') >= 0 ||
    lbl.indexOf('Changes in operating') >= 0 ||
    lbl.indexOf('Other operating adjustments') >= 0);
}

export function buildTableRow(
  label: string,
  amounts: string[],
  multi: boolean,
  totalRow: boolean,
  sectionRow: boolean,
): TableRow {
  const borders = totalRow ? BORDER_DOUBLE_BOTTOM
    : sectionRow ? BORDER_BOTTOM
    : BORDER_NONE;

  const labelCell = new TableCell({
    borders,
    width: { size: 50, type: WidthType.PERCENTAGE },
    children: [
      new Paragraph({
        children: [
          new TextRun({
            text: label,
            bold: totalRow || sectionRow,
            size: 20,
            font: 'Calibri',
            color: sectionRow && !totalRow ? '64748B' : '1E293B',
          }),
        ],
      }),
    ],
  });

  const amtCells = amounts.map(
    (amt) =>
      new TableCell({
        borders,
        width: { size: multi ? 25 : 50, type: WidthType.PERCENTAGE },
        children: [
          new Paragraph({
            alignment: AlignmentType.RIGHT,
            children: [
              new TextRun({
                text: amt,
                bold: totalRow,
                size: 20,
                font: 'Calibri',
              }),
            ],
          }),
        ],
      }),
  );

  return new TableRow({ children: [labelCell, ...amtCells] });
}

export function headerRow(labels: string[], multi: boolean): TableRow {
  return new TableRow({
    children: labels.map(
      (lbl, idx) =>
        new TableCell({
          borders: BORDER_BOTTOM,
          width: {
            size: idx === 0 ? 50 : multi ? 25 : 50,
            type: WidthType.PERCENTAGE,
          },
          children: [
            new Paragraph({
              alignment: idx === 0 ? AlignmentType.LEFT : AlignmentType.RIGHT,
              children: [
                new TextRun({
                  text: lbl,
                  bold: true,
                  size: 18,
                  font: 'Calibri',
                  color: '64748B',
                }),
              ],
            }),
          ],
        }),
    ),
  });
}

export function aoaToTable(dataRows: (string | number)[][], multi: boolean): Table {
  const colCount = multi ? 3 : 2;
  const tblRows: TableRow[] = [];

  // Column headers
  if (multi) {
    const m = meta();
    const curYear = (m.period ? extractYear(m.period) : null) || 'Current';
    const priorYear = (m.priorPeriod ? extractYear(m.priorPeriod) : null) || 'Prior';
    tblRows.push(headerRow(['', curYear, priorYear], multi));
  } else {
    tblRows.push(headerRow(['', 'Amount'], multi));
  }

  dataRows.forEach((row) => {
    const label = String(row[0] ?? '');
    const total = isTotal(label);
    const section = isSectionHeader(label);

    const amounts: string[] = [];
    for (let i = 1; i < colCount; i++) {
      const val = row[i];
      if (val === undefined || val === null || val === '') {
        amounts.push('');
      } else if (typeof val === 'number') {
        amounts.push(fmtAmt(val));
      } else {
        amounts.push(String(val));
      }
    }

    tblRows.push(buildTableRow(label, amounts, multi, total, section));
  });

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: tblRows,
  });
}
